import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { CheckCircle2, Loader2, CreditCard, Receipt, Mail } from "lucide-react";

const PLANS = [
  { name: "Starter", tag: "For solo operators", features: ["1 affiliate account", "50 AI articles/mo", "Basic analytics"] },
  { name: "Professional", tag: "Recommended", features: ["10 affiliate accounts", "500 AI articles/mo", "Priority network detection", "Advanced analytics"] },
  { name: "Business", tag: "Multi-brand", features: ["Unlimited accounts", "Unlimited AI content", "Team seats", "White-label reports"] },
  { name: "Enterprise", tag: "Custom scale", features: ["Everything in Business", "SLA + priority support", "Custom AI models", "API access"] },
];

export default function Subscriptions() {
  const [sub, setSub] = useState(null);
  const [packages, setPackages] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [cycle, setCycle] = useState("yearly");
  const [loadingKey, setLoadingKey] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [s, p, r] = await Promise.all([
        api.get("/subscription"),
        api.get("/payments/packages"),
        api.get("/payments/receipts"),
      ]);
      setSub(s.data);
      setPackages(p.data);
      setReceipts(r.data);
    } catch (e) {
      console.error(e);
    }
  };
  useEffect(() => { load(); }, []);

  const findPackage = (planName, c) => packages.find((p) => p.plan === planName && p.cycle === c);

  const startCheckout = async (planName) => {
    setErr(""); setLoadingKey(planName);
    try {
      const pkg = findPackage(planName, cycle);
      if (!pkg) throw new Error("Package not found");
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoadingKey(null);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="border-b border-slate-800 pb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Billing</p>
        <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">Plans</h1>
        <p className="mt-3 text-neutral-400">Real Stripe checkout — use test card <span className="font-mono text-sky-300">4242 4242 4242 4242</span> · any future expiry · any CVC.</p>
      </div>

      <div className="flex flex-wrap items-center gap-6 border border-slate-800 rounded-xl p-5">
        <div>
          <p className="font-mono text-[10px] uppercase text-neutral-500">Current plan</p>
          <p className="font-serif text-xl text-sky-100">
            {sub?.current_plan || "—"} <span className="text-neutral-500 text-sm">/ {sub?.billing_cycle}</span>
            {sub?.payment_gateway === "Stripe" && (
              <span className="ml-2 text-[10px] font-mono uppercase text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">via Stripe</span>
            )}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase text-neutral-500 mb-1">Cycle</p>
            <div className="flex gap-2">
              {["monthly", "yearly"].map((c) => (
                <button
                  key={c}
                  data-testid={`cycle-${c}`}
                  onClick={() => setCycle(c)}
                  className={`px-3 py-1.5 text-xs rounded font-mono uppercase ${cycle === c ? "bg-sky-400 text-slate-950" : "border border-slate-800 text-neutral-400"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {err && <div data-testid="checkout-error" className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {PLANS.map((plan) => {
          const active = sub?.current_plan === plan.name;
          const pkg = findPackage(plan.name, cycle);
          const isLoading = loadingKey === plan.name;
          return (
            <div key={plan.name} data-testid={`plan-${plan.name.toLowerCase()}`} className={`border rounded-xl p-6 flex flex-col ${active ? "border-sky-400 bg-sky-500/5" : "border-slate-800"}`}>
              <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">{plan.tag}</p>
              <h3 className="font-serif text-2xl text-sky-100 mt-2">{plan.name}</h3>
              <p className="font-serif text-4xl text-sky-50 mt-4 font-numeric">
                ${pkg ? pkg.amount : "—"}
                <span className="text-sm text-neutral-500 font-sans">/{cycle === "monthly" ? "mo" : "yr"}</span>
              </p>
              <ul className="mt-5 space-y-2 text-sm text-neutral-400 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-sky-400 mt-0.5 flex-shrink-0" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid={`btn-checkout-${plan.name.toLowerCase()}`}
                onClick={() => startCheckout(plan.name)}
                disabled={active || isLoading}
                className={`mt-6 py-2.5 rounded-md text-sm font-medium transition flex items-center justify-center gap-2 ${
                  active
                    ? "bg-slate-800 text-neutral-500 cursor-not-allowed"
                    : "bg-sky-400 hover:bg-sky-300 text-slate-950"
                }`}
              >
                {active ? "Current" : isLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                ) : (
                  <><CreditCard className="w-4 h-4" /> Upgrade to {plan.name}</>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <div className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Receipt className="w-5 h-5 text-sky-400" />
            <h3 className="font-serif text-xl text-sky-100">Payment receipts</h3>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{receipts.length} on file</span>
        </div>
        {receipts.length === 0 ? (
          <p className="text-sm text-neutral-500">No receipts yet. Complete a checkout to see one here.</p>
        ) : (
          <div className="divide-y divide-neutral-800">
            {receipts.map((r, i) => (
              <div key={i} data-testid={`receipt-row-${i}`} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-sky-100 text-sm truncate">{r.subject}</p>
                  <p className="text-[11px] font-mono text-neutral-500 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.to}</span>
                    <span>{new Date(r.queued_at).toLocaleString()}</span>
                    <span className={
                      r.status === "sent" ? "text-emerald-400"
                      : r.status === "queued_log_only" ? "text-sky-300"
                      : "text-red-400"
                    }>
                      ● {r.status}
                    </span>
                    {r.status === "queued_log_only" && (
                      <span className="text-[10px] text-slate-600">(add RESEND_API_KEY to send)</span>
                    )}
                  </p>
                </div>
                <span className="font-numeric text-sky-300 flex-shrink-0">${r.amount?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
